import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Button, Icon, QrCode, buttonClasses } from '@forecastify/ui';
import {
  CONTROL_PANEL_PATHS,
  operatorEnrolmentSchema,
  type OperatorEnrolment,
} from '@forecastify/shared';
import { apiCall } from '../api';
import { useT, type MessageKey } from '../i18n';
import { SIGN_IN_PATH } from '../routes';
import {
  AuthCard,
  AuthInput,
  AuthLede,
  AuthSteps,
  CODE_INPUT,
  TextInput,
} from '../chrome/auth-card';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Enrolling the second factor: Kezdés · Kulcs · Ellenőrzés · Kész.          │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE ONLY WAY OUT OF "CREATED, NEVER ENROLLED"**, and reachable without a session for that
 * reason: an operator with no verified factor cannot sign in at all, with no grace period.
 *
 * ⚠️ **The first card asks for email and password, which the mockup's does not.** The mockup's
 * operator is already known; ours is not — enrolment happens before any session exists, and the
 * server's `enrolBegin` proves the password through GoTrue before it will mint a factor. Everything
 * after that card is the drawing's: the key with its QR and a copy button, the code, and done.
 *
 * ⚠️ **The secret is shown once.** The note under it says so, and says what happens if the phone is
 * lost — which is NOT "another operator resets it" as the mockup's copy has it: that stays a support
 * operation on the command line (`decisions.md` S-19), so the sentence says that instead.
 */

type Step = 0 | 1 | 2 | 3;

const STEP_LABEL: readonly MessageKey[] = [
  'enrol.step.begin',
  'enrol.step.key',
  'enrol.step.verify',
  'enrol.step.done',
];

export function EnrolScreen(): ReactNode {
  const t = useT();
  const [step, setStep] = useState<Step>(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [enrolment, setEnrolment] = useState<OperatorEnrolment | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const begin = (): void => {
    if (busy || email.trim() === '' || password === '') return;
    setBusy(true);
    setFailure(null);
    void apiCall<unknown>(CONTROL_PANEL_PATHS.enrol, {
      method: 'POST',
      body: JSON.stringify({ email: email.trim(), password }),
    }).then((result) => {
      setBusy(false);
      if (!result.ok) {
        setFailure(result.code === 'unreachable' ? t('error.unreachable') : t('enrol.refused'));
        return;
      }
      const parsed = operatorEnrolmentSchema.safeParse(result.value);
      if (!parsed.success) {
        setFailure(t('error.unknown'));
        return;
      }
      setEnrolment(parsed.data);
      setStep(1);
    });
  };

  const verify = (): void => {
    if (busy || enrolment === null) return;
    if (!/^\d{6}$/u.test(code)) {
      setFailure(t('signIn.codeShape'));
      return;
    }
    setBusy(true);
    setFailure(null);
    void apiCall(CONTROL_PANEL_PATHS.enrolVerify, {
      method: 'POST',
      body: JSON.stringify({
        email: email.trim(),
        password,
        factorId: enrolment.factorId,
        totpCode: code,
      }),
    }).then((result) => {
      setBusy(false);
      if (!result.ok) {
        setFailure(result.code === 'unreachable' ? t('error.unreachable') : t('enrol.refused'));
        setCode('');
        return;
      }
      setStep(3);
    });
  };

  return (
    <AuthCard screen="enrol" title={t('enrol.title')}>
      <div className="flex flex-col gap-3">
        <AuthSteps
          label={t('enrol.steps')}
          steps={STEP_LABEL.map((key) => t(key))}
          current={step}
        />
      </div>

      {step === 0 ? (
        <form
          className="contents"
          onSubmit={(event) => {
            event.preventDefault();
            begin();
          }}
        >
          <AuthLede>{t('enrol.lede')}</AuthLede>
          <AuthInput label={t('signIn.email')}>
            <TextInput
              type="email"
              autoComplete="username"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
            />
          </AuthInput>
          <AuthInput label={t('signIn.password')} error={failure}>
            <TextInput
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
              }}
            />
          </AuthInput>
          <Button
            type="submit"
            size="lg"
            className="w-full"
            disabled={busy || email.trim() === '' || password === ''}
          >
            {busy ? t('enrol.beginWorking') : t('enrol.begin')}
          </Button>
        </form>
      ) : null}

      {step === 1 && enrolment !== null ? (
        <>
          <p className="text-muted-foreground text-[12.5px] leading-[1.55]">
            {t('enrol.scanHint')}
          </p>
          <div className="flex items-start gap-4">
            <div className="shrink-0">
              <QrCode value={enrolment.uri} label={t('enrol.qrLabel')} size={132} />
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <span className="text-muted-foreground text-[12px] font-medium">
                {t('enrol.secret')}
              </span>
              <code className="bg-muted rounded-md px-2.5 py-2 font-sans text-[13px] tracking-[.08em] break-all tabular-nums">
                {enrolment.secret.replace(/(.{4})/gu, '$1 ').trim()}
              </code>
              <Button
                variant="outline"
                size="sm"
                className="h-7 self-start px-2.5"
                onClick={() => {
                  void navigator.clipboard.writeText(enrolment.secret).then(() => {
                    setCopied(true);
                  });
                }}
              >
                <Icon name="copy" size={13} />
                {copied ? t('enrol.copied') : t('enrol.copy')}
              </Button>
            </div>
          </div>
          <p className="text-muted-foreground border-t pt-3 text-[12px] leading-[1.5]">
            {t('enrol.secretNote')}
          </p>
          <Button
            size="lg"
            className="w-full"
            onClick={() => {
              setStep(2);
            }}
          >
            {t('signIn.continue')}
          </Button>
        </>
      ) : null}

      {step === 2 ? (
        <form
          className="contents"
          onSubmit={(event) => {
            event.preventDefault();
            verify();
          }}
        >
          <p className="text-muted-foreground text-[12.5px]">{t('enrol.verifyLede')}</p>
          <AuthInput label={t('signIn.code')} error={failure}>
            <TextInput
              className={CODE_INPUT}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="000000"
              value={code}
              onChange={(event) => {
                setCode(event.target.value.replace(/\D/gu, '').slice(0, 6));
                setFailure(null);
              }}
            />
          </AuthInput>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="ghost"
              size="lg"
              onClick={() => {
                setFailure(null);
                setStep(1);
              }}
            >
              {t('signIn.back')}
            </Button>
            <Button type="submit" size="lg" className="flex-1" disabled={busy}>
              {busy ? t('enrol.verifyWorking') : t('enrol.verify')}
            </Button>
          </div>
        </form>
      ) : null}

      {step === 3 ? (
        <>
          <div className="flex items-start gap-3">
            <span className="grid size-8 shrink-0 place-items-center rounded-full bg-[var(--status-ok-soft)] text-[var(--status-ok-text)]">
              <Icon name="shield-check" size={16} />
            </span>
            <div className="flex flex-col gap-1">
              <h2 className="text-[14px] font-medium">{t('enrol.doneTitle')}</h2>
              <p className="text-muted-foreground text-[12.5px] leading-[1.5]">{t('enrol.done')}</p>
            </div>
          </div>
          <Link to={SIGN_IN_PATH} className={buttonClasses('default', 'lg', 'w-full')}>
            {t('enrol.backToSignIn')}
          </Link>
        </>
      ) : null}
    </AuthCard>
  );
}
